import type { QueryClient, UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarFinancingLockReaders } from "./solar-financing-lock-readers"
import type { SolarConcertApplicationStatusDto } from "../dto/solar-concert.dto"

/**
 * Approval rewrites the Quote with the approved terms (the investment
 * section, Share, the offers and the savings freshness read it) and locks the
 * selections, which every read behind the financing lock reports. Not awaited.
 */
const invalidateApplicationReaders = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  for (const queryKey of [
    queryKeys.solarProposal.pricing(proposalId),
    queryKeys.solarProposal.share(proposalId),
    queryKeys.solarProposal.concertOffers(proposalId),
    queryKeys.solarProposal.savings(proposalId),
  ]) {
    void queryClient.invalidateQueries({ queryKey, exact: true })
  }
  invalidateSolarFinancingLockReaders(queryClient, proposalId)
}

/** Every Concert application command answers with the status it just wrote. */
export const applySolarConcertApplicationStatus = (
  queryClient: QueryClient,
  proposalId: string,
  status: SolarConcertApplicationStatusDto,
): void => {
  queryClient.setQueryData(
    queryKeys.solarProposal.concertApplication(proposalId),
    status,
  )
  invalidateApplicationReaders(queryClient, proposalId)
}

/**
 * A failed command, or a gateway notification, may still have moved the
 * application (failed / uncertain / a Concert decision): read it all again.
 */
export const invalidateSolarConcertDependents = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.solarProposal.concertApplication(proposalId),
    exact: true,
  })
  invalidateApplicationReaders(queryClient, proposalId)
}

/**
 * A Concert application command: it answers with the status it wrote, and a
 * failure reads everything again unless `isAnswer` says the error never
 * reached the server.
 */
export const useSolarConcertStatusCommand = <
  TVariables extends { proposalId: string },
>(
  mutationFn: (
    variables: TVariables,
  ) => Promise<SolarConcertApplicationStatusDto>,
  { isAnswer }: { isAnswer?: (error: unknown) => boolean } = {},
): UseMutationResult<SolarConcertApplicationStatusDto, unknown, TVariables> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (status, { proposalId }) => {
      applySolarConcertApplicationStatus(queryClient, proposalId, status)
    },
    onError: (error, { proposalId }) => {
      if (isAnswer?.(error)) {
        return
      }
      invalidateSolarConcertDependents(queryClient, proposalId)
    },
  })
}
